import { useCallback, useEffect, useMemo, useRef } from 'react';
import { trpc } from '@client-trpc';
import {
  SignalBlockTarget,
  TemplateConfigInput,
  TemplateType
} from '@server/trading-templates/schemas/input.schema';
import {
  useAdditionalTradingFormContext,
  useTradingFormContext
} from '@client/widgets/trading-form/trading-form-context';
import { IndicatorsPath } from '@client/widgets/trading-form/indicators/indicator-config-form.utils';
import { TradingMode } from '@shared/types';
import {
  TemplateApplyMode,
  useTemplateEvents
} from '../template-events';
import { StrategyPath } from '@client/widgets/trading-form/strategy/strategy-config-form.utils';
import { SignalCondition } from '@server/data-feed/schemas/input.schema';
import type { TradingStrategy } from '@client/widgets/trading-form/trading-form.types';

type TemplateConfigMap = {
  market: {
    path: 'marketConfig';
    mode: TradingMode;
  };
  positionManagement: {
    path: 'positionManagementConfig';
  };
  indicators: {
    path: IndicatorsPath;
  };
  strategy: {
    path: StrategyPath;
  };
  signalBlock: {
    path: StrategyPath;
  };
};

type UseTemplatesProps = {
  [K in TemplateType]: {
    type: K;
  } & TemplateConfigMap[K];
}[TemplateType] & { timeframeIndex?: number; enabled?: boolean };

type StrategyBucket = 'entries' | 'exits';
type StrategySide = NonNullable<NonNullable<TradingStrategy['value']>['long']>;

const clone = <T,>(value: T): T => structuredClone(value);

const parseSignalBlockTarget = (
  target: SignalBlockTarget
): {
  side: 'long' | 'short';
  bucket: StrategyBucket;
} => {
  const [side, bucket] = target.split('.') as [
    'long' | 'short',
    StrategyBucket
  ];
  return { side, bucket };
};

const mergeSignalConditions = (
  currentConditions: SignalCondition[] | undefined,
  nextConditions: SignalCondition[],
  applyMode: TemplateApplyMode
) => {
  if (applyMode === 'replace') {
    return clone(nextConditions);
  }

  return [...clone(currentConditions ?? []), ...clone(nextConditions)];
};

const mergeStrategySide = (
  currentSide: StrategySide | undefined,
  nextSide: StrategySide | undefined
): StrategySide | undefined => {
  if (!currentSide && !nextSide) {
    return undefined;
  }

  return {
    entries: [...clone(currentSide?.entries ?? []), ...clone(nextSide?.entries ?? [])],
    exits: [...clone(currentSide?.exits ?? []), ...clone(nextSide?.exits ?? [])]
  };
};

const mergeStrategyConfig = (
  currentStrategy: TradingStrategy | undefined,
  nextStrategy: TradingStrategy
): TradingStrategy => {
  const mergedValue = {
    long: mergeStrategySide(currentStrategy?.value?.long, nextStrategy.value?.long),
    short: mergeStrategySide(currentStrategy?.value?.short, nextStrategy.value?.short)
  };

  const hasSignals = Boolean(mergedValue.long || mergedValue.short);

  return {
    plot: currentStrategy?.plot ?? nextStrategy.plot,
    visualization:
      currentStrategy?.visualization ?? nextStrategy.visualization,
    ...(hasSignals ? { value: mergedValue } : {})
  };
};

export const useTemplates = (props: UseTemplatesProps) => {
  const { type, timeframeIndex, enabled = true } = props;
  const effectiveTimeframeIndex = timeframeIndex ?? 0;
  const { setValue, getValues, watch } = useTradingFormContext();
  const { mode, defaultTemplatePolicy } = useAdditionalTradingFormContext();
  const { templateApplied$, emitTemplateApplied } = useTemplateEvents();
  const timeframes = watch('timeframes');
  const { data: defaultTemplates = [] } =
    trpc.tradingTemplates.defaultTemplates.useQuery(undefined, { enabled });
  const marketPath = props.type === 'market' ? props.path : undefined;
  const positionManagementPath =
    props.type === 'positionManagement' ? props.path : undefined;
  const indicatorsPath = props.type === 'indicators' ? props.path : undefined;
  const strategyPath = props.type === 'strategy' ? props.path : undefined;
  const signalBlockPath = props.type === 'signalBlock' ? props.path : undefined;
  const defaultTemplateForType = useMemo(
    () =>
      defaultTemplates.find((template) => template.config.type === type),
    [defaultTemplates, type]
  );
  const prevTimeframeCount = useRef(0);
  const initializedTimeframeCount = useRef(false);

  const applyTemplate = useCallback(
    (
      template: TemplateConfigInput,
      strategyTarget?: SignalBlockTarget,
      applyMode: TemplateApplyMode = 'replace'
    ) => {
      if (template.type !== type) return;

      switch (template.type) {
        case 'market':
          if (!marketPath || !mode) return;
          setValue(marketPath, {
            ...clone(template.value),
            tradingMode: mode
          });
          return;
        case 'positionManagement':
          if (!positionManagementPath) return;
          setValue(positionManagementPath, clone(template.value));
          return;
        case 'indicators':
          if (!indicatorsPath) return;
          setValue(
            indicatorsPath,
            applyMode === 'add'
              ? [
                  ...clone(getValues(indicatorsPath) ?? []),
                  ...clone(template.value)
                ]
              : clone(template.value)
          );
          return;
        case 'strategy':
          if (!strategyPath) return;
          setValue(
            strategyPath,
            applyMode === 'add'
              ? mergeStrategyConfig(
                  getValues(strategyPath),
                  clone(template.value) as TradingStrategy
                )
              : clone(template.value)
          );
          return;
        case 'signalBlock': {
          if (!signalBlockPath || !strategyTarget) return;
          const currentStrategy = getValues(signalBlockPath) ?? undefined;
          const { side, bucket } = parseSignalBlockTarget(strategyTarget);
          const bucketSignals = clone(template.value.conditions) as SignalCondition[];
          const currentSide = currentStrategy?.value?.[side];

          const nextValue: NonNullable<TradingStrategy['value']> = {
            ...(currentStrategy?.value ?? {})
          };

          nextValue[side] = {
            entries:
              bucket === 'entries'
                ? mergeSignalConditions(
                    currentSide?.entries,
                    bucketSignals,
                    applyMode
                  )
                : clone(currentSide?.entries ?? []),
            exits:
              bucket === 'exits'
                ? mergeSignalConditions(
                    currentSide?.exits,
                    bucketSignals,
                    applyMode
                  )
                : clone(currentSide?.exits ?? [])
          };

          setValue(signalBlockPath, {
            ...currentStrategy,
            value: nextValue
          });
          return;
        }
      }
    },
    [
      getValues,
      indicatorsPath,
      marketPath,
      mode,
      positionManagementPath,
      signalBlockPath,
      setValue,
      strategyPath,
      type
    ]
  );

  const tryApplyDefaultTemplate = useCallback(() => {
    if (!defaultTemplateForType) return;

    emitTemplateApplied({
      templateId: defaultTemplateForType.id,
      templateName: defaultTemplateForType.name,
      templateType: type,
      applyMode: 'replace',
      timeframeIndex: effectiveTimeframeIndex,
      templateConfig: defaultTemplateForType.config
    });
  }, [defaultTemplateForType, effectiveTimeframeIndex, emitTemplateApplied, type]);

  useEffect(() => {
    if (!enabled) return;

    const subscription = templateApplied$.subscribe((payload) => {
      if (payload.templateConfig.type !== type) return;
      if (payload.timeframeIndex !== effectiveTimeframeIndex) return;

      applyTemplate(
        payload.templateConfig,
        payload.strategyTarget,
        payload.applyMode
      );
    });

    return () => subscription.unsubscribe();
  }, [applyTemplate, effectiveTimeframeIndex, enabled, templateApplied$, type]);

  useEffect(() => {
    if (!enabled) return;
    if (defaultTemplatePolicy === 'SKIP_AUTO_APPLY_DEFAULT_TEMPLATES') return;
    if (type === 'signalBlock') return;
    if (!defaultTemplateForType) return;

    const currentCount = timeframes?.length ?? 0;
    const isTimeframeScopedType = type === 'indicators' || type === 'strategy';

    if (!initializedTimeframeCount.current) {
      initializedTimeframeCount.current = true;

      if (!isTimeframeScopedType) {
        tryApplyDefaultTemplate();
        prevTimeframeCount.current = currentCount;
        return;
      }

      const isLastTimeframeInstance =
        timeframeIndex !== undefined && timeframeIndex === currentCount - 1;

      if (isLastTimeframeInstance) {
        tryApplyDefaultTemplate();
      }

      prevTimeframeCount.current = currentCount;
      return;
    }

    const hasNewTimeframe = currentCount > prevTimeframeCount.current;
    prevTimeframeCount.current = currentCount;

    if (!hasNewTimeframe || !isTimeframeScopedType) return;

    const lastTimeframeIndex = currentCount - 1;
    if (timeframeIndex !== undefined && timeframeIndex !== lastTimeframeIndex) {
      return;
    }

    tryApplyDefaultTemplate();
  }, [
    defaultTemplateForType,
    defaultTemplatePolicy,
    enabled,
    timeframeIndex,
    timeframes,
    tryApplyDefaultTemplate,
    type
  ]);
};
